import { message } from "antd";
import config from "../../../config.json";
import {
  postRequest,
  patchRequest,
  formatRequestError,
} from "../../../utils/requests";

/**
 * Загрузка статусов монтажных заявок с бэка + фильтрация по isHasMontage.
 *
 * @param {string|string[]} state
 * @param {string|string[]} status
 * @param {string} query
 * @param {function(Array):void} setSpecifications — записать в таблицу
 * @param {function(boolean):void} setIsLoading
 * @param {function(number):void} setTotalCount — общее число заявок (для пагинации)
 * @param {number} page
 * @param {number} size
 * @returns {Promise<{application:Array, totalCount:number, currentPage:number}>}
 */
export function fetchStatuses(
  state = "NEW",
  status = "",
  query = "",
  setSpecifications = () => {},
  setIsLoading = () => {},
  setTotalCount = () => {},
  page = 1,
  size = 20
) {
  setIsLoading(true);

  const url =
    `${config.service}/api/appl/state?search=${encodeURIComponent(query)}`;
  const body = { state, page, size };
  if (status) {
    body.status = status;
  }

  return postRequest(url, body)
    .then((response) => {
      // Всё что пришло от бэка
      const rawApps = Array.isArray(response.data.application)
        ? response.data.application
        : [];

      // Отбираем только с монтажом
      const montageApps = rawApps.filter((app) => app.isHasMontage === true);

      // Отрисовываем эту фильтрацию
      setSpecifications(montageApps);

      // Но totalCount берём из ответа, не из montageApps.length
      setTotalCount(response.data.totalCount ?? 0);

      return {
        application: montageApps,
        totalCount: response.data.totalCount ?? 0,
        currentPage: response.data.currentPage ?? page,
      };
    })
    .catch((err) => {
      let msg = "Неизвестная ошибка";
      if (err?.response?.data?.message) msg = err.response.data.message;
      message.error(msg);
      throw err;
    })
    .finally(() => {
      setIsLoading(false);
    });
}


/**
 * Функция для принятия заявки на монтаж.
 */
export const acceptForInstallation = (id, onFinish) => {
  const url = `${config.service}/api/appl/changeState/${id}`;
  const body = {
    state: "ACTIVE",
    status: "INSTALLING",
  };

  const response = patchRequest(url, body);

  const hideLoadingMsg = message.loading("Загрузка...", 0);
  response
    .then(() => {
      message.success("Готово", 3);
    })
    .catch((err) => {
      message.error(formatRequestError("Ошибка", err), 3);
    })
    .finally(() => {
      hideLoadingMsg();
      if (onFinish) onFinish();
    });

  return response;
};

/**
 * Функция для отправки кода установки клиенту.
 */
export const sendInstallationCodeToClient = (id, phone, onSuccess, onFinish) => {
  const url = `${config.service}/api/appl/verificationCode/${id}`;
  const body = {
    phone: phone,
    type: "INSTALLATION",
  };

  return postRequest(url, body) // Возвращаем промис
    .then(data => {
      if (data?.status === 200) {
        onSuccess(data?.data || '');
      }
    })
    .catch((e) => {
      let msg = "Неизвестная ошибка";
      if (e?.response?.data?.message) {
        msg = e.response.data.message;
      }
      message.error(msg);
      throw e; // Пробрасываем ошибку для дальнейшей обработки
    })
    .finally(() => {
      if (onFinish) {
        onFinish();
      }
    });
};

/**
 * Функция для завершения установки с вводом PIN-кода.
 */
export const completeInstallation = (pin, lead, onSuccess, onError, onFinish) => {
  const url = `${config.service}/api/appl/verifyInstallationCode/${lead._id}`;
  const body = {
    id: lead._id,
    code: pin,
    phone: lead.client[0].phone,
  };

  postRequest(url, body)
    .then(data => {
      console.log('message', data.data);
      if (data?.status === 200) {
        onSuccess(data?.data || '');
      }
    })
    .catch((e) => {
      console.log('error while completing installation', e);
      onError();
    })
    .finally(() => {
      if (onFinish) {
        onFinish();
      }
    });
};

/**
 * Функция для принудительного завершения монтажа.
 * @param {string} id - Идентификатор заявки.
 * @returns {Promise} - Возвращает промис с результатом запроса.
 */
export const forceCompleteInstallation = (id) => {
  const url = `${config.service}/api/appl/force-complete-install/${id}`;
  return postRequest(url, {})
    .then((data) => {
      if (data?.status === 200) {
        return data?.data || '';
      }
      throw new Error(data?.data?.message || "Неизвестная ошибка");
    })
    .catch((error) => {
      let msg = "Неизвестная ошибка";
      if (error?.response?.data?.message) {
        msg = error.response.data.message;
      }
      message.error(msg);
      throw error;
    });
};
